import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithAuth } from "./baseQuery";

const baseQueryWithProfiles = (args, api, extraOptions) => {
    if (typeof args === 'string') {
        args = { url: args };
    }
    args.url = `/profiles${args.url ? `/${args.url}` : ''}`;
    return baseQueryWithAuth(args, api, extraOptions);
};

/**
 * The backend migration to modules/company-profile/ (see backend commit
 * "migrate backend to the modules/ pattern") changed the list response from
 * a flat {message, data, total, page, limit, totalPages} envelope to
 * {success, message, data, meta: {total, page, limit, totalPages, ...}}.
 * Every consumer in this client was built against the old flat shape (some
 * read `.total` directly, GET /profiles' own pre-migration response used yet
 * a third shape, {pagination: {...}}) - rather than hunt down and edit every
 * call site, flatten `meta` back onto the top level here so `.total`,
 * `.page`, etc. and `.pagination` all keep resolving exactly as before.
 */
const normalizeListResponse = (response) => ({
    ...response,
    data: response?.data ?? [],
    total: response?.meta?.total,
    page: response?.meta?.page,
    limit: response?.meta?.limit,
    totalPages: response?.meta?.totalPages,
    pagination: response?.meta,
});

export const companyProfileApi = createApi({
    reducerPath: "companyProfileApi",
    baseQuery: baseQueryWithProfiles,
    tagTypes: ["CompanyProfile"],
    endpoints: (builder) => ({
        getCompanyProfiles: builder.query({
            async queryFn({ pageSize = 200, page = 1 } = {},
                queryApi,
                extraOptions,
                baseQuery
            ) {
                // pageSize/nextToken were this app's own naming, never
                // anything the backend understood - GET /profiles has only
                // ever taken page/limit (limit capped at 100 server-side).
                const result = await baseQuery({
                    url: "",
                    params: { limit: pageSize, page },
                });
                if (result.error) return result;
                return { data: normalizeListResponse(result.data) };
            },
            providesTags: ["CompanyProfile"],
            keepUnusedDataFor: 0,
        }),
        getUserProfiles: builder.query({
            query: (searchQuery) => ({
                url: "",
                params: searchQuery ? { search: searchQuery } : {},
            }),
            transformResponse: normalizeListResponse,
            providesTags: ["CompanyProfile"],
        }),
        getAllCompanyProfiles: builder.query({
            async queryFn({ searchQuery = "", createdFrom = "", createdTo = "", status = "", page = 1, limit = 100 } = {}, queryApi, extraOptions, baseQuery) {
                // GET /profiles/all was collapsed into GET / during the
                // backend migration - the combined filter set (search/
                // createdFrom/createdTo/status) that /all used to be the
                // only one supporting now works on the base route too.
                const result = await baseQuery({
                    url: "",
                    params: {
                        ...(searchQuery && { search: searchQuery }),
                        ...(createdFrom && { createdFrom }),
                        ...(createdTo && { createdTo }),
                        ...(status && { status }),
                        page,
                        limit,
                    },
                });
                if (result.error) return result;
                return { data: normalizeListResponse(result.data) };
            },
            providesTags: (result, error, arg) => {
                const { searchQuery = "", createdFrom = "", createdTo = "", status = "", page = 1 } = arg || {};
                return searchQuery || createdFrom || createdTo || status || page > 1
                    ? [{ type: "CompanyProfile", id: `SEARCH-${searchQuery}-${createdFrom}-${createdTo}-${status}-${page}` }]
                    : ["CompanyProfile"];
            },
            refetchOnMountOrArgChange: true,
        }),
        getCompanyProfileById: builder.query({
            query: (id) => `${id}`,
            providesTags: ["CompanyProfile"],
        }),
        getComplianceStatusBulk: builder.query({
            query: () => "compliance-status",
            providesTags: ["CompanyProfile"],
        }),
        getComplianceStatusById: builder.query({
            query: (id) => `${id}/compliance-status`,
            providesTags: (result, error, id) => [{ type: "CompanyProfile", id }, "CompanyProfile"],
        }),
        createCompanyProfile: builder.mutation({
            query: (data) => ({
                url: "",
                method: "POST",
                body: data,
            }),
            invalidatesTags: ["CompanyProfile"],
        }),
        updateCompanyProfile: builder.mutation({
            // PUT /:id is pure JSON now (no multer) - contact_persons/
            // waste_streams are real nested arrays that never survived
            // multipart/form-data as actual JSON, so the backend migration
            // split file uploads out to their own endpoint (see
            // uploadCompanyProfileDocument below) rather than accepting
            // FormData here at all. `body` must be a plain object.
            query: ({ id, body }) => ({
                url: `${id}`,
                method: "PUT",
                body,
            }),
            invalidatesTags: ["CompanyProfile"],
        }),
        uploadCompanyProfileDocument: builder.mutation({
            // documentType is one of "kra-pin" | "coi" | "nema" | "contract".
            query: ({ id, documentType, file }) => {
                const formData = new FormData();
                formData.append("file", file);
                return {
                    url: `${id}/documents/${documentType}`,
                    method: "PUT",
                    body: formData,
                };
            },
            invalidatesTags: ["CompanyProfile"],
        }),
        updateCompanyProfileStatus: builder.mutation({
            query: ({ id, status, reason }) => ({
                url: `${id}/status`,
                method: "PUT",
                body: { status, reason },
            }),
            invalidatesTags: ["CompanyProfile"],
        }),
        deleteCompanyProfile: builder.mutation({
            query: (id) => ({
                url: `${id}`,
                method: "DELETE",
            }),
            invalidatesTags: ["CompanyProfile"],
        }),
    }),
});

export const {
    useGetCompanyProfilesQuery,
    useGetAllCompanyProfilesQuery,
    useLazyGetAllCompanyProfilesQuery,
    useGetCompanyProfileByIdQuery,
    useGetComplianceStatusBulkQuery,
    useGetComplianceStatusByIdQuery,
    useGetUserProfilesQuery,
    useCreateCompanyProfileMutation,
    useUpdateCompanyProfileMutation,
    useUploadCompanyProfileDocumentMutation,
    useUpdateCompanyProfileStatusMutation,
    useDeleteCompanyProfileMutation,
} = companyProfileApi;
